/**
 * Intent Detection for Session Mode Routing
 *
 * Determines target session mode based on user message content.
 * Runs BEFORE session creation - conservative matching defaults to ADVICE.
 *
 * NOT a general router: this is two narrow fast-paths (DISCOVERY keywords,
 * practice-build phrasing) layered in front of ADVICE, the default for
 * everything else — including former check-in and "insights" phrasing,
 * which now scope normally in ADVICE rather than misrouting into the
 * practice builder or a retired mode. See plan
 * `project-docs/plans/advice-scoping-routing.md` Phase R3.
 *
 * @module utils/intentDetection
 */

/**
 * Keyword patterns for DISCOVERY mode detection
 * @private
 */
const DISCOVERY_PATTERNS = [
  /\b(discovery|questionnaire|assessment)\b/i,
  /\b(self[- ]?discovery|explore myself|understand myself)\b/i,
  /\b(keep working on|continue|resume).*(questionnaire|assessment)/i
];

/**
 * Keyword patterns for TREATMENT mode detection.
 *
 * Deliberately narrow to the practice-BUILD fast-path only (composePractice
 * is `phase === 'PATH_WORK'`-gated — this is a working one-click entry into
 * the builder). Check-in phrasing ("my practices", "check in on my
 * practice", "practice journey") used to also match here and misrouted into
 * the builder instead of a check-in; it now falls through to the ADVICE
 * default, where `navigateTo` (app-tools) reaches the /practices page.
 * @private
 */
const TREATMENT_PATTERNS = [
  /\b(work on|build)\s+(an?\s+)?(practices?|experiments?)\b/i
];

/**
 * Tests if message matches any pattern in the given array
 * @private
 * @param {string} message - Message to test
 * @param {RegExp[]} patterns - Array of patterns to test against
 * @returns {boolean} True if any pattern matches
 */
function matchesPatterns(message, patterns) {
  return patterns.some(pattern => pattern.test(message));
}

/**
 * Check if message matches DISCOVERY intent
 * @param {string} message - Normalized message text
 * @returns {boolean}
 */
function matchesDiscoveryIntent(message) {
  return matchesPatterns(message, DISCOVERY_PATTERNS);
}

/**
 * Check if message matches TREATMENT intent
 * @param {string} message - Normalized message text
 * @returns {boolean}
 */
function matchesTreatmentIntent(message) {
  return matchesPatterns(message, TREATMENT_PATTERNS);
}

/**
 * Determines target session mode based on user message.
 * Runs BEFORE session creation. Conservative matching — defaults to ADVICE.
 *
 * @param {string} message - User's input message
 * @returns {string} Target mode: 'DISCOVERY', 'TREATMENT', or 'ADVICE'
 *
 * @example
 * detectSessionIntent('I want to explore myself') // → 'DISCOVERY'
 * detectSessionIntent('build a practice')          // → 'TREATMENT'
 * detectSessionIntent('How do I handle anxiety?')  // → 'ADVICE'
 * detectSessionIntent('check in on my practice')   // → 'ADVICE'
 * detectSessionIntent('')                          // → 'ADVICE'
 */
export function detectSessionIntent(message) {
  if (!message?.trim()) {
    return 'ADVICE';
  }

  const normalized = message.trim();

  if (matchesDiscoveryIntent(normalized)) {
    return 'DISCOVERY';
  }

  if (matchesTreatmentIntent(normalized)) {
    return 'TREATMENT';
  }

  // Default to ADVICE (handles ~90% of cases, incl. former check-in/insight phrasing)
  return 'ADVICE';
}
